// Construction du texte dicte.
//
// DEUX PIEGES PAYES EN PRODUCTION, sur le meme telephone :
//
// 1. (2026-08-25) `resultIndex` vaut presque toujours zero sur Chrome Android et
//    `results` contient tout depuis le debut : ajouter « ce qui suit l'indice »
//    recollait tout le deja-ecrit. Regle : ne jamais accumuler, RECONSTRUIRE depuis
//    l'indice 0 a chaque evenement.
//
// 2. (2026-08-26) Sur ce meme Android, les morceaux de `results` ne sont PAS des
//    segments successifs mais des INSTANTANES CUMULATIFS : « demain », « demain les »,
//    « demain les enfants »... Les recoller donnait « demain demain les demain les
//    enfants... » — la capture d'ecran du porteur. Sur Chrome de bureau, au contraire,
//    ce sont bien des segments disjoints qu'il faut recoller.
//
// La regle qui couvre les deux mondes : FUSIONNER au lieu de concatener. Si le nouveau
// morceau contient deja tout ce qu'on a (il commence par l'accumule), c'est un
// instantane : il REMPLACE. Sinon c'est un segment : il s'AJOUTE.

export interface SegmentDictee {
  isFinal: boolean;
  0: { transcript: string };
}

export interface ResultatsDictee {
  length: number;
  [index: number]: SegmentDictee;
}

export interface Transcription {
  /** Segments que le moteur considere comme acquis. */
  definitif: string;
  /** Segments encore susceptibles de changer : affiches, jamais memorises. */
  provisoire: string;
}

function normaliser(texte: string): string {
  return texte.replace(/\s+/g, ' ').trim();
}

/**
 * Ajoute un morceau au texte accumule, en reconnaissant les instantanes cumulatifs.
 * La comparaison ignore la casse : le moteur change parfois la majuscule initiale
 * entre deux instantanes.
 */
function fusionner(accumule: string, morceau: string): string {
  const nouveau = normaliser(morceau);
  if (nouveau === '') return accumule;
  if (accumule === '') return nouveau;
  if (nouveau.toLowerCase().startsWith(accumule.toLowerCase())) return nouveau;
  return `${accumule} ${nouveau}`;
}

export function construireTranscription(resultats: ResultatsDictee): Transcription {
  let definitif = '';
  let provisoire = '';

  // Depuis 0, TOUJOURS : c'est ce qui rend la fonction idempotente.
  for (let i = 0; i < resultats.length; i += 1) {
    const segment = resultats[i];
    if (segment === undefined) continue;
    const morceau = segment[0]?.transcript ?? '';
    if (segment.isFinal) definitif = fusionner(definitif, morceau);
    else provisoire = fusionner(provisoire, morceau);
  }

  return { definitif, provisoire };
}

/**
 * Assemble le texte affiche : ce qui precede la dictee, puis ce qui vient d'etre dit —
 * avec la MEME fusion, parce qu'apres un arret spontane du moteur, l'instantane suivant
 * peut reprendre tout le debut de phrase.
 */
export function assembler(...morceaux: string[]): string {
  return morceaux.reduce((accumule, morceau) => fusionner(accumule, morceau), '');
}
